import type {
  DirectiveResult,
  SYSFeatures,
  SYSLongRunningProcess,
  TemplateResult,
} from "../../"
import {
  SYSFormValidationValidators,
  html,
  ifDefined,
  repeat,
  unsafeHTML,
} from "../../"
import { T, getDateValue, isMobile } from "../../global/globals"
import {
  HookFieldGetLabelRender,
  HookTHGroupGetLabelRender,
  HookTHGroupGetLabelRender_ReturnType,
  HookTHGroupGetLabelRender_Type,
  HookTableClicked,
  // HookTableCustomFilterDescription,
  // HookTableCustomFilterDescription_ReturnType,
  // HookTableCustomFilterDescription_Type,
  HookTableCustomTitle,
  HookTableDragDrop,
  HookTableDragDrop_Type,
  HookTablePrepareFn,
  HookTableRenderingAfterRender,
  HookTableRenderingFirstUpdated,
  HookTableRenderingUpdated,
  HookViewHeaderCellClass,
} from "../../global/hooks"

import type {
  HookFieldGetLabelRender_Type,
  HookFieldGetList_ReturnType,
  HookTableClicked_Type,
  HookTableCustomTitle_Type,
  HookTablePrepareFn_ReturnType,
  HookTablePrepareFn_Type,
  HookTableRenderingAfterRender_Type,
  HookTableRenderingFirstUpdated_Type,
  HookTableRenderingUpdated_Type,
  HookViewHeaderCellClass_ReturnType,
  HookViewHeaderCellClass_Type,
  RowFnConfirmContentFieldsReset_Type,
  ServerData_ChangedType,
  TableRowFnProcessFeedbackFull_Type,
  TableRowFnProcessRawFeedbackSummaryCount_Type,
  _HookTableClickedExecutionToSetType,
  _TableGroupColumnsToSetType,
} from "../../global/hooks"

import type {
  SYSDialog,
  SYSEditor,
  SYSFeature,
  SYSPermissions,
  SYSTableDefIds,
} from "../../index"
import { SYSConfig } from "../../index"

import type { ListState } from "../forms/controls/listcontrol"
import type {
  DataType,
  FieldFormat,
  FieldSchema,
  LookupColumnDef,
  SYSFormField,
  Schema,
  _ClientConversion,
} from "../forms/sysformelement"
import { SYSBaseElement } from "../sysbaseelement"
import {
  CachedRendererData,
  GetRendererFn,
  GetRowDisplayValue,
  _GetAlign,
  _GetFilterSortDifferentiator,
  _GetFilterSortMainDifferentiator,
  _GetTableDialogTitle,
  rowControlActionsHandler,
} from "./helpers"

import { Data } from "../../dataapi"
import type { SYSPermissionModifiers } from "../../state"
import { DialogCurrent, DialogOk } from "../dialog/dialogapi"
import { DialogType } from "../dialog/sysdialogbase"
import { ScreenCurrentState } from "../screenapi"
import { TableInitResetStrategy } from "./actions"
import type { NavDef } from "./navcontrol"
import type { TableRowDef } from "./rowwrapper"
import { _TableDefSelectRow, TableGet, TableSelectRow } from "./tableapi"
import { _FilterSortChanged } from "./tablerowheadermenu/tablesettings/helpers"
import { AppDefTableDefId } from "../../config"

export type DragDropEventType = "dragover" | "dragstart" | "drop"

export type RowState = "Add" | "Update" | "Ok"

export type SaveMode = "add" | "update"

export type DisplayMode =
  | "Table"
  | "ColumnFilter"
  | "Detailview"
  | "Editor"
  | "DialogTitle"
  | "HeaderMenu"
export type EditDisplayMode = "Edit" | "EditInline"

export type RowFunctionResultToSet = {
  // input = id from db table... returns rowKey
  setSuccess?: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => string
  setWarning?: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => string
  setInformation?: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => string

  setError?: (
    id: string,
    text?: string | TemplateResult | DirectiveResult,
    code?: string,
    cnt?: TableRowFnProcessRawFeedbackSummaryCount_Type
  ) => string
  setFromResult?: (
    result: TableRowFnProcessFeedbackFull_Type
  ) => Promise<string>
}

export type SortDirection = "asc" | "desc"

export type SYSTableData<T = any> = {
  data?: { [key: string]: T }
  hasMoreDataOnServer?: boolean
  schema?: { [key: string]: Schema }
  checkForOtherModification?: boolean
  clientConversion?: _ClientConversion<T>
  tableDef: { [key: string]: SYSTableDef }
  timestamp?: number
  offline?: {
    addedKeys: { [key: string]: number }
    updatedKeys: { [key: string]: { columns: {}; timestamp: number } }
    deletedKeys: {}
    errors: {}
    offlineHandlerInProcess: boolean
  }
  offlineSeq?: number
  index?: {}
  lookups?: { [key in string]: ListDefinition }
  // to place random data in the same call as TableRefresh(..)
  ext?: any
}

export type TableDataAndDef = {
  def: SYSTableDef
  data: SYSTableData
}

export type TableProps = {
  def: SYSTableDef
  data: SYSTableData
}

export type ViewRowClassType = {
  className?: string
  tooltip?: string
  draggable?: boolean
}

export type StaticFilter = {
  [key: string]: any
}

export type _TableGroupAggregateResultType = {
  Sum: any
  Count: number
  Min: any
  Max: any
  Avg: number
  LastRowKey: string
}

export const _TableGetGroupInitAggregateResult =
  (): _TableGroupAggregateResultType => {
    return {
      Count: 0,
      Sum: undefined,
      Min: undefined,
      Max: undefined,
      Avg: 0,
      LastRowKey: undefined,
    }
  }

export const _TableGroupCalcAggregate = (
  currentResult: _TableGroupAggregateResultType,
  currentVal: any,
  rowKey: string
): _TableGroupAggregateResultType => {
  let isString = typeof currentVal === "string"
  let count = currentResult.Count + 1

  if (currentResult.Sum === undefined) {
    if (isString) {
      currentResult.Sum = ""
    } else {
      currentResult.Sum = 0
    }
  }
  let sum = currentResult.Sum + currentVal
  let min
  if (currentVal < currentResult.Min || currentResult.Min === undefined) {
    min = currentVal
  }
  let max
  if (currentVal > currentResult.Max || currentResult.Max === undefined) {
    max = currentVal
  }
  let avg
  if (!isString) {
    avg = sum / count
  }
  return {
    Count: count,
    Sum: sum,
    Min: min,
    Max: max,
    Avg: avg,
    LastRowKey: rowKey,
  }
}

export type RowFnTrackingInterval =
  | "None"
  | "Every2Secs"
  | "Every5Secs"
  | "Every10Secs"
  | "Every20Secs"

export type RowControlAction = {
  mode?: "Hidden" | "Row" | "Table"

  visibleOnColumns?: string[] | "All"

  fnKey?: string
  translationKey?: string
  icon?: string
  visualGroup?: string
  process?: "None" | SYSLongRunningProcess
  trackData?: RowFnTrackingInterval
  prompt?: {
    // OnlyWhenConfirmHookFound = Usecase OvlCopy (Normally no prompt but there should be the possibility to cancel a copy)
    type?: "None" | "WhenMulti" | "Always" | "WhenMultiOrHookResult"
    connectedForm?: {
      formFields: { [key: string]: SYSFormField }
      fieldsResetType: RowFnConfirmContentFieldsReset_Type
    }
  }
  endpoint?: {
    url: string
  }
  blocking?: {
    group: "edit" | "view"
  }
  cancelable?: boolean
  handleFeedback?: boolean
  summary?: "None" | "Snack" | "DialogAlways" | "DialogIfMultiple"
  whenMultiple?: {
    callForEveryRow: boolean
    optionalPositionTranslationCode?: string
    grouped?: {
      byColumn: string
    }
  }
  permission?: SYSPermissions
  permissionModifier?: SYSPermissionModifiers
  role?: SYSFeatures
}

export type OvlSysColumn = {
  editRow?: SelectedEditRow
  rowState?: {
    state: RowState
    originalId: any
    lastChangeBelongsToTableDefId: string
  }
  fnFeedback?: { [key: string]: TableRowFnProcessFeedbackFull_Type }
  lastFeedbackTS?: number
}

export type ColumnAction = {
  fnKey: string
  translationKey?: string
  icon?: string
  rowFnProcess?: string
}

export type SelectedRow = {
  selected: boolean
  count: number
  showNav: boolean
}

export type EditMode = "edit" | "copy" | "add"

export type SelectedEditRow = {
  selected: boolean
  mode: EditMode
  // when duplicating contains the source key
  fromKey?: string
}

export type SelectedViewRow = {
  selected: boolean
}

export type TableRowDetailViewMode = "None" | "Enabled" | "EnabledOnlyMobile"

// a group is just a key followed by a Result object (Which should be type casted by the user because we can save SumX,AvgY, ... on it)
// to setup calculations we need a HookTableGroup_%_Aggregate Hook defined which does the calc
// the result gets saved in result where key is the Expression Value
// for every key there is expected to be a HookTableGroup_%_Expression Hook which determines the Value and lastValue and see if it changes
// it also sets a Key Object so we can quickly check on every row which Group was changed
// depending if there are hooks HookTableGroup_%_HeaderDisplay resp. a HookTableGroup_%_FooterDisplay a sep Summary row can be displayed

export type GroupBreakType = {
  groupValue: string
}

type TableGroupAggregate = {
  disabled?: boolean
  // objects of array because we could add more features in aggregates like (e.g. NeedsSum, NeedsMin to optimize calc time)
  // and in the groups maybe a enabled true/false
  aggregates?: { aggregateCode: string }[]
  groups?: { groupCode: string; respectInSort?: "ForceGroup" }[]

  onlyApplyWhenColumnSortStartsWith?: string[]
  onlyApplyWhenCustomSort?: string[]
}

export type _ColumnFnDefType = {
  // dynamically displays menuentry in headerform

  // each group will hav its own <ul> tag and group as classname (css grid for 4 color selection eg. hedin Customer Column)
  visualGroup?: string
  // only display this fns when at least 1 row is selected
  onlyWhenRowsSelected?: boolean
  // only valid for
  permission?: SYSPermissions
  permissionModifier?: SYSPermissionModifiers
  role?: SYSFeatures
  // fns
  fns: ColumnAction[]
}[]
export type _SelectionClippingType = "Remove" | "Keep" | "Prompt"

export type _RebuildType = {
  deletedKeys?: { [key: string]: boolean }
  addedKeys?: { [key: string]: boolean }
  updatedKeys?: { [key: string]: boolean }
  needsRefresh?: boolean
  changed: { [key: string]: ServerData_ChangedType }
}

export type SYSTableDef<
  RowFnsType extends "OvlCopy" | "OvlEdit" | string | number | symbol = any,
> = {
  initialised?: boolean
  columnsInitialisedFromSchema?: boolean
  permission?: SYSPermissions
  id?: SYSTableDefIds
  title?: {
    show?: boolean
    translationKey?: string
    invalidFavoritDisplayMode?: "Remove" | "Mark"
    entityTitle?: {
      entityNameTranslationKey?: string
      tableDialogTitleTranslationKey?: string
      tableDialogTitleDoNotDisplayId?: boolean
    }
  }
  feature?: SYSFeature
  server?: {
    endpoint: string
  }
  database?: {
    dataIdField?: string
    dbInsertMode?: DBInsertMode
    // disableConstraintViolationSnacks?: boolean
    // constraintViolationBehaviour?: "Continue" | "Cancel"
    useSchema?: boolean
    // automatically create AND .Where expressions for all found columnnames in filter
    applyEntityFilter?: boolean
  }
  dataFetching?: {
    useCustomDataFetching?: boolean
  }
  columns: SYSColumnsDef

  uiState?: {
    //hasGroupsOrAggregates?: boolean
    hasGroupedHeader?: boolean
    hasEditableColumns?: boolean
    columnsRenderingState?: {
      columns: {
        [key: string]: {
          visibility?: _ColumnVisibility
          alignCss?: string
        }
      }
      visibleInTable?: string[]
    }
    groupHeaderFooterResultToSet?: _TableGroupColumnsToSetType

    headerSelected?: string
    rebuild?: _RebuildType
    dataFilteredAndSorted?: string[]
    rowsCount?: number
    selectedRow?: { [key: string]: SelectedRow }
    selectedCounter?: number

    // if we di inlineediting we need to know ass well which button/thing ws focused before jumoing into it
    editInlineLastFocus?: string
    currentlyAddingKey?: string
    lastRowKey?: string
    tableFilterClass?: string
    currentSelectedFilterSort?: string
    currentSelectedFilterSortDesc?: string
    currentSelectedFilterSortValue?: string
    currentSelectedFilterSortStore?: { [key: string]: string }
    sort?: {
      sort?: Sort
      sortCustom?: { sorts: { [key: string]: CustomSort }; selected: string }
      byFeedback?: boolean
    }
    filter?: {
      filter?: Filter
      filterCustom?: { [key: string]: { active: boolean } }
      //1st key is rowAttributes key
      filterAttributes?: {
        //attributes just containss the keys of the selected attributes
        [key: string]: { attributes: { [key: string]: boolean } }
      }
      columns?: {
        [key: string]: {
          columnFilter?: _ColumnColumnFilter
          rangeFilter?: {
            selected: RangeFilterType
            defaultFilterType?: RangeFilterType

            val?: any
            val2?: any
          }
        }
      }
    }
    paging?: Paging
    firstVisibleColumn?: string
    formType?: string
    formId?: string
    // the group breaks are saved as Group defined in Options (second key) and Key of row (first key)
    tableGroupHeaderBreaks?: {
      [key: string]: {
        isBreak?: boolean
        isFirstRow?: boolean
        isLastRow?: boolean
        groups?: { [key: string]: GroupBreakType }
        groupAccessStrings?: string[]
      }
    }
    tableGroupFooterBreaks?: {
      [key: string]: {
        isBreak?: boolean
        isFirstRow?: boolean
        isLastRow?: boolean
        groups?: { [key: string]: GroupBreakType }
        groupAccessStrings?: string[]
      }
    }

    // its used to save the results per Group as defined in Options (first key)  and GroupExpression Value (second key))
    tableGroupResults?: {
      [key: string]: {
        [key: string]: any
      }
    }
    _resetStrategyForPersistFilterSort?: TableInitResetStrategy
    _lastFilterSortDifferentiator?: any
    _schemaRetrieved?: boolean
  }
  options?: {
    feedback?: {
      level: "0:none" | "1:error" | "2:warning" | "3:information" | "4:success"
    }
    filterSortSettings?: {
      doNotPersist?: boolean
      staticFilterKey?: string
      showInTitle?: boolean
      selectionClipping?: _SelectionClippingType
      enableFavorites?: boolean
    }
    nullHandling?: {
      defaultDisplayValue: string
      nullValidatorToUse?: SYSFormValidationValidators
    }
    forceKeepFormStateOnCancel?: boolean
    addedRowsPosition?: "bottom" | "top"
    /*copyColumnsIgnore?: { [key: string]: string }*/
    customRowActions?: {
      [key in RowFnsType]?: RowControlAction
    }

    customColumnActions?: {
      // dynamically displays menuentry in headerform
      // 1st key is columnKey put "All" for columnKey if i should be available for all columns
      [key: string]: _ColumnFnDefType
    }
    initial_rowAttributes?: RowAttributes
    labelAndColumnTitleHandling?: {
      translationPrefix?: string
      fallbackTranslationPrefix?: string
    }
    navType?: "top/bottom" | "top" | "bottom" | "custom"

    initial_sort?: InitialSort
    initial_sortCustom?: {
      sorts: { [key: string]: CustomSort }
      selected: string
    }
    initial_paging?: Paging
    initial_filter?: Filter
    initial_filterCustom?: { [key: string]: TableDefCustomFilter }
    /*tabs?: Tabs*/
    edit?: {
      customCaption?: {
        editTranslationKey?: string
        copyTranslationKey?: string
        addTranslationKey?: string
        translationKey?: string
      }
      doNotDisplayDefaultDialogTitle?: boolean
      editType?: "inline" | "big"
      groups?: EditGroups
      noCancelOnEditFormIfFormStateWasInvalidWhenOpened?: boolean
      doFormChangedForEveryFieldInInit?: boolean
      displayValueHintWhileTyping?: boolean
      reserveSpaceForInlineValidationSummary?: boolean
    }

    // view?: {
    //   customCaption?: { translationKey: string }
    //   viewType?: "default" | "custom"
    // }
    controlsRendering?: {
      checkbox?: {
        view?: { checked: string; unchecked: string }
        table?: { checked: string; unchecked: string }
      }
      list?: {
        displayCachedListsWhileTyping?: boolean
      }
      fillSpaceIfNotVisible?: boolean
      useAlignRight?: boolean

      // use with care. if text is selected drag and drop of that selected text will be active...
      selectIfFocused?: boolean
    }
    tableGroupAggregates?: TableGroupAggregate
  }
  features?: {
    stickyHeader?: boolean
    detailView?: TableRowDetailViewMode
    page?: boolean
    multiSelect?: boolean
    displayMultiSelectEndSelectButton?: "TouchDevice" | "All" | "None"
    edit?: boolean
    multiEdit?: boolean
    add?: boolean
    copy?: boolean
    multiCopy?: boolean
    delete?: boolean
    multiDelete?: boolean
    filter?: boolean
    multiFilter?: boolean

    headerMenu?: boolean
    showMoreButton?: boolean
    // put a 0 if it always should be refreshed, put a -1 if never, other numbers > 0 are considered as seconds
    // this is taken into account when the refresh button is clicked
    forceFreshServerDataIfOlderThan?: number
    showFooterButtons?: boolean
    showRefreshButton?: "TableNav" | "None"
    focusToFirstEditableField?: boolean
    autoCloseButtonIfInsideDialog?: boolean
    saveLocallyMode?: "SaveLocally"
    autoRebuild?: "AfterModifyOrDelete" | "AfterModifyOrDeleteOrAdd"
  }
}

export type EditRowDef = {
  tableDef: SYSTableDef
  key: string
  row: {}
  data: SYSTableData
  mode: EditMode
  DialogType?: SYSDialog
}

export type ViewRowDef<RowType = any> = {
  tableDef: SYSTableDef
  key: string
  row: RowType
  data: SYSTableData
  columnsAlign: {}
  columnsVisible: {}
}

export type EditRowSaveCancelDef = {
  tableDef: SYSTableDef
  key: string
  row: {}
  data: SYSTableData
}

export type SelectRowDefMode = "Select" | "Unselect" | "Mode1"

export type SelectRowDefOptions = {
  mode?: SelectRowDefMode
  modifiers?: TableClickModifiersType
  e?: Event
  _innerEvent?: boolean
}

export type SelectRowDef = {
  key: string
  def: SYSTableDef
  dataDef: SYSTableData
  options?: SelectRowDefOptions
}

export type EditGroups = {
  [key: number]: {
    translationKey?: string
    className?: string
  }
}

export type RowAttributes = {
  [key: string]: {
    translationKey?: string
    showInTitle?: boolean
    // an icon which represents the type of attributes
    icon?: string
    // if true calculates a count (occurence of this attribut in the current filtered result) which gets handed over to the GetAttributeDescription Hook so it can display something like AttributeX (11 <- count)
    useCount?: boolean
    // if the columns involved should also be colored blue to directly see that column is filtered
    involvedColumnKeys?: { [key: string]: boolean }
    // set to true if the filter functionality should just be displayed in the involved columns
    displayOnlyInInvolvedColumnKeys?: boolean

    filter?: {
      //@@todos for later, not implemented yet
      //threshold for displaying a extended filter screen
      useExtendedDialogStartingFrom?: number
    }
    rowFn?: {
      //@@todos for later, not implemented yet
      //properties for displaying a rowFn
      // (they will have RowFn[%Key] as Key)
    }
    attributes?: { [key: string]: boolean }
  }
}

export type Tabs = {
  edit: {
    selected?: number
    tabs: {
      [key: number]: {
        translationKey: string
        className?: string
        hasCustomContent?: boolean
      }
    }
  }
  view: {
    selected?: number
    tabs: {
      [key: number]: {
        translationKey: string
        className?: string
        hasCustomContent?: boolean
      }
    }
  }
}

export type ControlType =
  | "text"
  | "textarea"
  | "list"
  | "checkbox"
  | "option"
  | "date"
  | "datetime"
  | "time"
  | "asset"
export type _ColumnAlign = "left" | "center" | "right"

export type SYSColumnsDef = {
  [key: string]: SYSColumnDef
}

export type Sort = {
  field: string
  direction: SortDirection
  useValue?: boolean
}

type InitialSort = {
  field: string
  direction: SortDirection
  fieldMobile?: string
  directionMobile?: SortDirection
  useValue?: boolean
}

type CustomSort = {
  translationKey: string
  // if the sort functionality should just be displayed in specific columns
  displayOnlyInColumnKeys?: string[]
  showInTitle: boolean
  visualGroup?: string
}

type Filter = {
  value?: string
  trimValue?: boolean
  showSelected?: boolean
  // use the static portion of  filter to define tablerow to table relations (1:n)
  static?: StaticFilter
  forcedFilterKeys?: string[]
  forcedFilterContextTitle?: string
}

export type TableDefCustomFilter = {
  translationKey?: string
  showInTitle: boolean

  group?: number
  visualGroup?: string
  // if the columns involved should also be colored blue to directly see that column is filtered
  involvedColumnKeys?: { [key: string]: boolean }
  // set to true if the filter functionality should just be displayed in the involved columns
  displayOnlyInInvolvedColumnKeys?: boolean
  active: boolean
}

export type Paging = {
  page: number
  pageSize: number
}

export type HeaderClick = {
  key: string
  def: SYSTableDef
  data: SYSTableData
}

export type SortClick = {
  key: string
  def: SYSTableDef
  data: SYSTableData
  ascending: boolean
  useValue: boolean
}

export type FilterClick = {
  value: string
  def: SYSTableDef
  data: SYSTableData
}

export type DBInsertMode =
  | "UDTAutoNumber"
  | "UDTAutoGUID"
  | "UDTAutoNumberBoth"
  | "UDTAutoGUIDBoth"
  | "UDTManual"
  | "AutoIdentity"
  | "AutoGUID"
  | "Manual"

export type _ColumnVisibility =
  | "Table"
  | "TableView"
  | "TableNotMobile_Edit_View"
  | "TableOnlyMobile"
  | "Table_Edit_View"
  | "Edit_View"
  | "View"
  | "Edit"
  | "None"

export type EditorType = {
  id: SYSEditor
  translationPrefix?: string
  config?: any
}

export type SYSInputMode =
  | "none"
  | "text"
  | "decimal"
  | "numeric"
  | "tel"
  | "search"
  | "email"
  | "url"
export type SYSLookupsToDataMapping = {
  // key = lookup key
  // inner key = lookup column key
  [key: string]: { [key: string]: { dataColumnKey: string } }
}
export type SYSColumnDef = {
  control?: ControlType
  type?: DataType
  order?: number

  list?: ListState
  sortable?: boolean
  filter?: {
    rangeFilter?: {
      selected: RangeFilterType
      defaultFilterType?: RangeFilterType
      val?: any
      val2?: any
    }
    columnFilter?: _ColumnColumnFilter
  }

  nullHandling?: {
    // if enabled then control can show a reset button (if implemented)
    enabled?: boolean
    // used for tabledisplay
    displayValue?: string
    // used for ValidateDataType
    setToNullIf?: any
    setToValueIfNull?: any
  }

  schema?: FieldSchema

  groups?: {
    tab?: number
    group?: number
  }
  ui?: {
    align?: _ColumnAlign
    labelTranslationKey?: string
    thGroupTranslationKey?: string
    format?: FieldFormat
    isPassword?: boolean
    option?: { inline?: boolean }
    expandTableCellOnClick?: boolean
    // can not be changed by user and will be displayed greyed out
    readonly?: boolean
    // if true it can not be set to visible/readonly/... useing the StateSet Methods
    alwaysReadOnly?: boolean
    // if true it will not affect dirty. (used in hedin for a calculated date range selector within the form)

    visibility?: _ColumnVisibility
    fillSpaceIfNotVisible?: boolean
    translationVisibility?: _ColumnVisibility
    //showLabelIfNoValueInView?: boolean
    checkedValue?: any
    uncheckedValue?: any
    language?: string
    viewTab?: number
    useBrowserDatePicker?: boolean
    displayEmptyIfValue?: any
    displayValueHintWhileTyping?: boolean
    sortByValue?: boolean
    selectIfFocused?: boolean
    infoValueTranslationCode?: string
    noLabel?: boolean
    inputMode?: SYSInputMode
    addIgnoreFocusClass?: boolean
  }

  editor?: EditorType
}

export type ColumnDisplayDef = {
  type?: DataType
  ui?: { format?: FieldFormat; displayEmptyIfValue?: any }
  list?: ListState
}

export type LookupsType = { [key: string]: HookFieldGetList_ReturnType }

export type ListDefinitionSortFnType = { [key: string]: any }
export type ListDefinition = {
  data?: {
    [key: string]: any
  }
  lookupDef?: { [key: string]: LookupColumnDef }
  // sortKeys-> used for sorting data
  sortFn?: (
    dataA: ListDefinitionSortFnType,
    dataB: ListDefinitionSortFnType
  ) => number
  sortMode?: "HitLengthThenCustom" | "CustomThenHitLength"
  //nullHandling?: LookupNullHandlingType
}

export type RangeFilterType =
  | "none"
  | "equal"
  | "greaterThanOrEqual"
  | "lessThanOrEqual"

export type ColumnFilter = {
  showFilter?: boolean
  top: number
  sortBy?: "Count" | "Value" | "DisplayValue" | "Original"
  sortOrder?: "Ascending" | "Descending"
  enabled?: boolean
  selected?: any
  isEmptySelected?: boolean
  customVal1InitValue?: any
}

export type _ColumnFilterTypes = "none" | "list"

export type _ColumnColumnFilter = {
  additionalColumn?: string
  type?: _ColumnFilterTypes
  exclusive?: boolean
  list?: any[]
}

export type Value = {
  value: any
  displayValue: string | TemplateResult
  count: number
}

export let cachedRendererFn: Map<string, CachedRendererData> = new Map<
  string,
  CachedRendererData
>()
export type TableClickModifiersType = {
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
}

export class SYSTableHeader extends SYSBaseElement {
  props: any
  tabledata: TableProps
  intersectionObserver: IntersectionObserver

  dragAndDropHookFn: any

  tableAfterRenderHookFn: any
  tableFirstUpdatedHookFn: any
  tableUpdatedHookFn: any
  lastDefId: any
  defId: any
  handleNavClick = (e: Event) => {
    e.stopPropagation()
    e.preventDefault()
  }

  handleTHGroupColumnClick = async (e) => {
    e.preventDefault()
    let def = this.tabledata.def

    if (def.features.headerMenu) {
      let def = this.tabledata.def
      this.actions.ovl.internal._TableSelectHeader({
        def,
        data: this.tabledata.data,
        key: def.uiState.firstVisibleColumn,
      })
      this.actions.ovl.table._OpenTableRowHeaderMenu({
        tableDefId: def.id,
      })
    }
  }

  handleHeaderColumnClick = async (e) => {
    let def = this.tabledata.def
    let currentModifiers: TableClickModifiersType = {
      altKey: e.altKey,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
    }

    let currentNode: HTMLElement = e.target

    let linkHandledByOvl = false
    let linkHandledBySystem = false
    let key

    for (let zz = 0; zz < 5; zz++) {
      if (!linkHandledByOvl) {
        linkHandledByOvl =
          currentNode.classList &&
          currentNode.classList.contains("ovl-hook-handled")
      }
      if (!linkHandledBySystem) {
        linkHandledBySystem =
          currentNode.classList &&
          currentNode.classList.contains("ovl-sys-handled")
      }
      key = currentNode.getAttribute("data-headercol")
      if (key) {
        break
      }
      currentNode = currentNode.parentElement
      if (!currentNode) {
        break
      }
    }

    if (linkHandledByOvl) {
      // helps with links
      e.preventDefault()
    }
    // we need to use this hook to do e.preventDefault, stopPropagation,...
    // it is important this one is first and sync
    let functionName = HookTableClicked
    let fn = this.actions.app.features[def.feature]?.hooks?.[functionName]
    const executionToSet: _HookTableClickedExecutionToSetType = {
      selectionLogic:
        linkHandledByOvl || linkHandledBySystem ? "Ignore" : "Mode1",
      expandTextLogic: linkHandledBySystem ? "Ignore" : "Default",
    }
    if (fn) {
      let value: HookTableClicked_Type = {
        columnKey: key,
        defId: def.id,
        e,
        executionToSet,
        modifiers: currentModifiers,
        linkType: linkHandledByOvl
          ? "OvlHook"
          : linkHandledBySystem
            ? "System"
            : "None",
        rowKey: undefined,
        row: undefined,
        expandClicked: false,
      }
      Object.defineProperty(value, "executionToSet", {
        value: executionToSet,
        writable: false,
        configurable: false,
        enumerable: true,
      })

      await fn(value)
    }
    if (executionToSet.selectionLogic === "Ignore") {
      return
    }
    if (def.features.headerMenu) {
      this.actions.ovl.internal._TableSelectHeader({
        def: this.tabledata.def,
        data: this.tabledata.data,

        key: key,
      })
      this.actions.ovl.table._OpenTableRowHeaderMenu({
        tableDefId: this.tabledata.def.id,
      })
    }
  }

  async init() {
    this.handleSourceData()
    await super.init()
  }
  async disconnectedCallback() {
    this.intersectionObserver.disconnect()
    this.intersectionObserver = null

    this.removeEventListener("click", this.handleRowClick)
    this.removeEventListener("keydown", this.handleKeyDown)
    if (this.dragAndDropHookFn) {
      this.removeEventListener("dragstart", this.handleDragStart)
      this.removeEventListener("dragover", this.handleDragOver)
      this.removeEventListener("drop", this.handleDrop)
      this.removeEventListener("mouseleave", this.removeDragOverClass)
      //this.removeEventListener("dragleave", this.handleDragLeave)
    }

    await super.disconnectedCallback()
  }

  handleSourceData = () => {
    let defId = this.props(this.state)
    if (defId !== this.lastDefId) {
      if (typeof defId === "object") {
        defId = defId.defId
      }
      let data: SYSTableData = SYSConfig.app.tableDefIds[defId].getData(
        this.state
      )
      this.tabledata = {
        data,
        def: data.tableDef[defId],
      }

      this.tableAfterRenderHookFn =
        this.actions.app.features?.[data.tableDef[defId].feature]?.hooks?.[
          HookTableRenderingAfterRender
        ]
      this.tableFirstUpdatedHookFn =
        this.actions.app.features?.[data.tableDef[defId].feature]?.hooks?.[
          HookTableRenderingFirstUpdated
        ]

      this.tableUpdatedHookFn =
        this.actions.app.features?.[data.tableDef[defId].feature]?.hooks?.[
          HookTableRenderingUpdated
        ]

      this.dragAndDropHookFn =
        this.actions.app.features?.[this.tabledata.def.feature]?.hooks?.[
          HookTableDragDrop
        ]
      if (this.dragAndDropHookFn) {
        this.removeEventListener("dragstart", this.handleDragStart)
        this.addEventListener("dragstart", this.handleDragStart)
        this.removeEventListener("dragover", this.handleDragOver)
        this.addEventListener("dragover", this.handleDragOver)
        this.removeEventListener("mouseleave", this.removeDragOverClass)
        this.addEventListener("mouseleave", this.removeDragOverClass)
        this.removeEventListener("drop", this.handleDrop)
        this.addEventListener("drop", this.handleDrop)
      }
      this.classList.add("ovl-tableview")
      if (this.lastDefId !== undefined) {
        this.classList.remove("ovl-table-" + this.lastDefId)
        this.classList.remove("ovl-tableview-" + this.lastDefId)
        let differentiator = _GetFilterSortDifferentiator(this.lastDefId)
        if (differentiator) {
          this.classList.remove("__differentiator_" + differentiator)
        }
        let mainDifferentiator = _GetFilterSortMainDifferentiator(
          this.lastDefId
        )
        if (mainDifferentiator) {
          this.classList.remove("__maindifferentiator_" + mainDifferentiator)
        }
      }
      this.classList.add("ovl-table-" + this.tabledata.def.id)
      this.classList.add("ovl-tableview-" + this.tabledata.def.id)
      let differentiator = _GetFilterSortDifferentiator(this.tabledata.def.id)
      if (differentiator) {
        this.classList.add("__differentiator_" + differentiator)
      }
      let mainDifferentiator = _GetFilterSortMainDifferentiator(
        this.tabledata.def.id
      )
      if (mainDifferentiator) {
        this.classList.add("__maindifferentiator_" + mainDifferentiator)
      }
      this.intersectionObserver = new IntersectionObserver(
        async (entries, observer) => {
          for (let i = 0; i < entries.length; i++) {
            let entry = entries[i]
            if (entry.intersectionRatio === 1) {
              // get data path
              //@ts-ignore
              let params = entry.target.dataLinkObject
              //@@todo format possibly changed. as soon as first time using this in a project rethink the naming
              if (params.cat !== "Link") {
                params["mode"] = "Thumb"
                let endpoint = params["endpoint"]
                let res = await Data(
                  `${this.state.ovl.apiUrl}${endpoint}/get`,
                  params,
                  { method: "GET", expectBlob: true }
                )
                if (res.data) {
                  const urlCreator = window.URL || window.webkitURL
                  //@ts-ignore
                  entry.target.src = urlCreator.createObjectURL(res.data)
                  observer.unobserve(entry.target)
                }
              } else {
                //@ts-ignore
                entry.target.src = params.val
                observer.unobserve(entry.target)
              }
            }
          }
        },
        {
          root: document.getElementById("ovl-intersectionobserver"),
          threshold: 1,
        }
      )
      this.lastDefId = defId
    }
  }

  async getUI() {
    // also handle props/source from getUi because we would like top use the same element for different tables depending on props defId
    this.handleSourceData()
    let def = this.tabledata.def
    let dataAndSchema = this.tabledata.data
    let columns = def.columns
    let columnsCount = 0

    let customRowActions = def.options.customRowActions
    let processesToTrackForRowFnHintDisplay = []
    if (customRowActions) {
      processesToTrackForRowFnHintDisplay = Object.keys(customRowActions)
        .filter(
          (k) =>
            customRowActions[k].handleFeedback &&
            customRowActions[k].process !== "None"
        )
        .map((k) => {
          return customRowActions[k]
        })
    }
    let customHeaderCellClasses: HookViewHeaderCellClass_ReturnType
    let functionName = HookViewHeaderCellClass.replace("{subFeature}", "")
    let fn = this.actions.app.features[def.feature]
    if (fn?.hooks?.[functionName]) {
      customHeaderCellClasses = fn.hooks[functionName](<
        HookViewHeaderCellClass_Type
      >{
        columns: def.columns,

        displayMode: <DisplayMode>"Table",
        feature: def.feature,
        tableDefId: def.id,
      })
    }
    if (!customHeaderCellClasses) {
      customHeaderCellClasses = {}
    }
    let visibleColumns = def.uiState?.columnsRenderingState?.visibleInTable
    if (!visibleColumns || visibleColumns.length === 0) {
      return undefined
    }
    let stickyTableHeader
    if (def.features.stickyHeader && SYSConfig.global?.stickyHeaderEnabled) {
      if (SYSConfig.global?.stickyHeaderEnabled(this.state)) {
        stickyTableHeader = "stickyTableHeader"
      }
    }
    let groupHeaderRow
    // just 2 maps which makes it easier afterwards to check if a column is a group start resp. end column (for the styles for the borders left/right)

    let thHeaderEnd = {}
    if (def.uiState.hasGroupedHeader) {
      let thGroups: { translationKey: string; colspan: number }[] = []
      let colspan = 0
      let thGroup = columns[visibleColumns[0]].ui.thGroupTranslationKey

      let oldK
      visibleColumns.forEach((k) => {
        if (thGroup === columns[k].ui.thGroupTranslationKey) {
          colspan++
        } else {
          thGroups.push({ colspan, translationKey: thGroup })
          thHeaderEnd[oldK] = true

          thGroup = columns[k].ui.thGroupTranslationKey
          colspan = 1
        }
        oldK = k
      })
      thGroups.push({ colspan, translationKey: thGroup })
      thHeaderEnd[oldK] = true

      groupHeaderRow = html`<tr
        class=${`fd-table__row ovl-tableview-thgrouprow thgrouprow__${def.id}`}
        @click=${this.handleTHGroupColumnClick}
      >
        ${thGroups.map((g) => {
          let rendererFn = GetRendererFn(
            def.feature,
            cachedRendererFn,
            HookTHGroupGetLabelRender,
            g.translationKey
          )

          let headerPart: HookTHGroupGetLabelRender_ReturnType = { res: "" }
          if (g.translationKey) {
            headerPart.res = T(g.translationKey)
          }
          if (rendererFn) {
            headerPart = rendererFn(<HookTHGroupGetLabelRender_Type>{
              caption: headerPart.res,
            })
          }

          return html`
            <th
              colspan="${g.colspan}"
              data-headercol=${g.translationKey}
              title=${ifDefined(headerPart.tooltip)}
              class=${`fd-table__cell   ovl-tableview-thgroupcell thgroupcell__${
                g.translationKey !== undefined ? g.translationKey : ""
              } ${headerPart.cssClass ? headerPart.cssClass : ""}}`}
              scope="col"
            >
              <span>${headerPart.res}</span>
            </th>
          `
        })}
      </tr>`
    }

    let headerRow = html`
      ${visibleColumns.map((k) => {
        let column = columns[k]
        let uiStateFilterColumn = def.uiState.filter.columns[k]
        let filterCustomInit = def.options.initial_filterCustom

        if (def.initialised && !def.uiState.firstVisibleColumn) {
          def.uiState.firstVisibleColumn = k
        }
        let sortdirection = ""
        let sortdirectionTitle = ""
        if (def.initialised) {
          let sortCustom = def.uiState.sort.sortCustom
          let hasCustomSort =
            sortCustom.sorts &&
            sortCustom.selected &&
            sortCustom.sorts[sortCustom.selected]
          if (
            def.uiState.sort.byFeedback === true &&
            def.uiState.firstVisibleColumn === k
          ) {
            sortdirection = "sap-icon--sys-enter-2"
            sortdirectionTitle = T("SYSHeaderMenuFeedbackSortTitle")
          } else if (column.sortable && !hasCustomSort) {
            if (k === def.uiState.sort.sort.field) {
              if (def.uiState.sort.sort.direction === "asc") {
                sortdirection = "sap-icon--sort-ascending"
              } else {
                sortdirection = "sap-icon--sort-descending"
              }
            }
          }
        }
        let cellBgColor = ""
        if (def.initialised) {
          let filterCustom = def.uiState.filter.filterCustom
          let filterAttributes = def.uiState.filter.filterAttributes
          let filterAttributesInit = def.options.initial_rowAttributes
          if (
            (uiStateFilterColumn.columnFilter &&
              uiStateFilterColumn.columnFilter.type !== "none") ||
            (uiStateFilterColumn.rangeFilter &&
              uiStateFilterColumn.rangeFilter.selected !== "none" &&
              uiStateFilterColumn.rangeFilter.val) ||
            (filterCustom &&
              Object.keys(filterCustom)
                .filter((f) => filterCustom[f].active)
                .some((s) => filterCustomInit[s].involvedColumnKeys?.[k])) ||
            Object.keys(filterAttributes)
              .filter(
                (f) => Object.keys(filterAttributes[f].attributes).length > 0
              )
              .some((s) => filterAttributesInit[s].involvedColumnKeys?.[k])
          ) {
            cellBgColor = "__filteractive"
          }
          if (k === def.uiState.headerSelected) {
            cellBgColor = `__selected ${cellBgColor}`
          }
        }
        columnsCount++
        let align: _ColumnAlign = "left"
        align = column.ui?.align
        let caption
        // if tranlsationPrefix then use prefix plus key
        if (!column.ui.noLabel) {
          if (column.ui && column.ui.labelTranslationKey) {
            caption = T(column.ui.labelTranslationKey)
          } else if (
            def.options.labelAndColumnTitleHandling?.translationPrefix !==
            undefined
          ) {
            if (
              this.state.ovl.language.translations[
                `${def.options.labelAndColumnTitleHandling.translationPrefix}${k}`
              ] !== undefined
            ) {
              caption = T(
                `${def.options.labelAndColumnTitleHandling.translationPrefix}${k}`
              )
            } else if (
              def.options.labelAndColumnTitleHandling
                ?.fallbackTranslationPrefix !== undefined
            ) {
              caption = T(
                `${def.options.labelAndColumnTitleHandling.fallbackTranslationPrefix}${k}`
              )
            } else {
              caption = T(
                `${def.options.labelAndColumnTitleHandling.translationPrefix}${k}`
              )
            }
          }
        }
        if (!caption) {
          caption = k
        }

        // check for custom header renderer
        let rendererFn = GetRendererFn(
          def.feature,
          cachedRendererFn,
          HookFieldGetLabelRender.replace("{subFeature}", ""),
          k
        )
        let headerPart
        if (!rendererFn) {
          headerPart = caption
        } else {
          headerPart = rendererFn(<HookFieldGetLabelRender_Type>{
            columnKey: k,
            caption,
            align,
            displayMode: <DisplayMode>"Table",
          }).res
        }
        if (typeof headerPart === "string") {
          headerPart = unsafeHTML(headerPart)
        }
        let customHeaderCellClass: string = ""
        let tooltip
        if (customHeaderCellClasses[k]) {
          customHeaderCellClass = customHeaderCellClasses[k].className
          tooltip = customHeaderCellClasses[k].tooltip
        }
        if (!def.uiState.columnsRenderingState.columns[k].alignCss) {
          def.uiState.columnsRenderingState.columns[k].alignCss = _GetAlign(
            column.ui.align
          )
        }
        return html`
          <th
            data-headercol=${k}
            title=${ifDefined(tooltip)}
            class=${`fd-table__cell ${
              def.uiState.columnsRenderingState.columns[k].alignCss
            } ${cellBgColor}    ovl-tableview-headercell ${
              def.uiState.hasGroupedHeader ? "thGroup" : ""
            } ${thHeaderEnd[k] ? "thGrp" : ""}  col__${k}  ctl__${
              column.control
            } tpe__${column.type} ${customHeaderCellClass} `}
            scope="col"
          >
            <span>${headerPart}</span>
            <span
              title=${sortdirectionTitle}
              class=${`ovl-tablesorticon ${sortdirection}`}
            ></span>
          </th>
        `
      })}
    `

    let colStyle: any = visibleColumns.map((k) => {
      return html`
        <col
          class=${`ovl-colstyle-${k} ctl__${columns[k].control} tpe__${
            columns[k].type
          } ${
            customHeaderCellClasses[k]
              ? customHeaderCellClasses[k].className
              : ""
          }`}
        />
      `
    })

    let rowNavTop
    if (
      def.initialised &&
      !def.uiState.headerSelected &&
      (def.options.navType === "top/bottom" || def.options.navType === "top")
    ) {
      rowNavTop = html`
        <ovl-tnavcontrol
          class="fd-table__row"
          .props=${() => {
            return {
              defId: this.props(this.state),
            } as NavDef
          }}
        ></ovl-tnavcontrol>
      `
    }
    let rowNavBottom
    let rows
    if (def.initialised) {
      let dataFilteredAndSorted = def.uiState.dataFilteredAndSorted
      // get all the committed rows (no temporary keys)
      let dataRows = dataFilteredAndSorted
      let filteredRowsCount = dataRows.length
      let paging = def.uiState.paging
      // do paging only on committed rows
      if (def.features.page) {
        dataRows = dataRows.slice(
          paging.page * paging.pageSize,
          (paging.page + 1) * paging.pageSize
        )
      }

      let dataAndAddRows = dataRows
      // make sure to show freshly and not refreshed (=resorted) rows on the first page or last page depending on mode
      let page = -1
      if (def.features.page) {
        page = Math.ceil(filteredRowsCount / paging.pageSize) - 1
        if (page < 0) {
          page = 0
        }
      }

      if (
        def.options.navType === "top/bottom" ||
        def.options.navType === "bottom"
      ) {
        rowNavBottom = html`
          <ovl-tnavcontrol
            class="fd-table__row ovl-nav-footer"
            .props=${() => {
              return { defId: this.props(this.state) } as NavDef
            }}
          ></ovl-tnavcontrol>
        `
      }
      let doNotRender = {}
      if (dataAndSchema.offline) {
        let delKeys = dataAndSchema.offline.deletedKeys
        if (delKeys) {
          Object.keys(delKeys).forEach((f) => {
            doNotRender[f] = true
          })
        }
      }

      if (
        def.uiState.currentlyAddingKey &&
        def.options.edit.editType !== "inline"
      ) {
        doNotRender[def.uiState.currentlyAddingKey] = true
      }

      dataAndAddRows = dataAndAddRows.filter((f) => !doNotRender[f])
      rows = repeat(
        dataAndAddRows,
        (k: string) => k,
        (k, i) => {
          return html`
            <ovl-trg
              .props=${() => {
                return <TableRowDef>{
                  data: dataAndSchema,
                  key: k,
                  index: i,
                  tableDef: def,
                  thHeaderEnd,
                  processesToTrackForRowFnHintDisplay,
                  selected: def.uiState.selectedRow[k],
                  tableGroupHeaderBreak: def.uiState.tableGroupHeaderBreaks[k],
                  tableGroupFooterBreak: def.uiState.tableGroupFooterBreaks[k],
                  intersectionObserver: this.intersectionObserver,
                }
              }}
            >
            </ovl-trg>
          `
        }
      )
    }
    let titleText
    let titleTemplate
    let title
    let hasSelectedFilterChanged = false
    if (def.uiState.currentSelectedFilterSort) {
      hasSelectedFilterChanged = _FilterSortChanged(def.id, true)
    }

    let titleTranslationKey = def.title.translationKey
    if (def.title.show) {
      if (titleTranslationKey) {
        titleText = T(titleTranslationKey)
        titleTemplate = `<span class="__title">${titleText}</span>`
      } else {
        titleText = ""
        titleTemplate = ""
      }
    }
    // check if filters and sort need to be put in title
    if (def.initialised) {
      let customFnHook =
        this.actions.app.features?.[def.feature]?.hooks?.[HookTablePrepareFn]
      let customFnHookResult: HookTablePrepareFn_ReturnType

      let filterCustom = def.uiState.filter.filterCustom
      let filterCustomInit = def.options.initial_filterCustom
      let sortCustom = def.uiState.sort.sortCustom
      let sortText
      let tooltipSort
      if (
        sortCustom.sorts &&
        sortCustom.selected &&
        sortCustom.sorts[sortCustom.selected]?.showInTitle
      ) {
        let values = {}
        values[sortCustom.selected] = {
          description: T(sortCustom.sorts[sortCustom.selected].translationKey),
        }

        if (customFnHook) {
          customFnHookResult = customFnHook(<HookTablePrepareFn_Type>{
            data: dataAndSchema,
            def,
            values,
            mode: "TableTitle",
            type: "Sort",
          })
        }
        sortText = customFnHookResult?.[sortCustom.selected]?.res
        if (!sortText) {
          sortText = values[sortCustom.selected].description
        }
        if (!sortText) {
          sortText = sortCustom.selected
        }
        tooltipSort = customFnHookResult?.[sortCustom.selected]?.tooltip
      }

      // create the customFn value for the customFn hook
      let filterCustomKeys = Object.keys(filterCustom)
      let values = filterCustomKeys
        .filter(
          (f) =>
            filterCustom[f].active &&
            (filterCustomInit[f].translationKey ||
              filterCustomInit[f].showInTitle)
        )
        .reduce((val, f) => {
          val[f] = { description: T(filterCustomInit[f].translationKey) }
          return val
        }, {})

      if (customFnHook) {
        customFnHookResult = customFnHook(<HookTablePrepareFn_Type>{
          data: dataAndSchema,
          def,
          values,
          mode: "TableTitle",
          type: "Filter",
        })
      }

      let filteractive = ""
      let filterTextAndTooltipArray = Object.keys(filterCustom)
        .filter(
          (f) =>
            filterCustom[f].active &&
            filterCustomInit[f].showInTitle &&
            customFnHookResult?.[f]?.visibility !== "NotVisible"
        )
        .map((m) => {
          filteractive = "__filteractive"
          let filterText = customFnHookResult?.[m]?.res
          if (!filterText) {
            filterText = values[m].description
          }
          if (!filterText) {
            filterText = m
          }
          let tooltip = customFnHookResult?.[m]?.tooltip

          let visibility = customFnHookResult?.[m]?.visibility

          let icon = customFnHookResult?.[m]?.icon

          return { filterText, tooltip, visibility, icon }
        })
      let filterTextArray = <string[]>(
        filterTextAndTooltipArray.map((m) => m.filterText)
      )
      let filterText = filterTextArray.join(", ")
      let tooltip = filterTextAndTooltipArray
        .filter((f) => f)
        .map((m) => m.tooltip)
        .join("\n")

      let filterAttributeInitDef = def.options.initial_rowAttributes
      let filterAttributeDef = def.uiState.filter.filterAttributes
      let filterAttributeKeys = Object.keys(filterAttributeDef)
      let tooltipAttributes = ""
      let filterAttributeArray = filterAttributeKeys
        .filter(
          (f) =>
            filterAttributeInitDef[f].showInTitle &&
            Object.keys(filterAttributeDef[f].attributes).length > 0
        )
        .map((defKey) => {
          //@@todo call GetAttributeProperties Hook in context "HeaderMenu" and build up tooltips and custom descs...
          return `<span class="ovlrowattrfiltertitletabletitle"
              >${
                filterAttributeInitDef[defKey].translationKey
                  ? T(filterAttributeInitDef[defKey].translationKey)
                  : defKey
              }

              (${Object.keys(filterAttributeDef[defKey].attributes)
                .map(
                  (attributeKey) =>
                    `<span class="ovlrowattrdesctabletitle">${attributeKey}</span>`
                )
                .join(", ")}</span
            >)`
        })

      let filterAttributeText
      if (filterAttributeArray.length > 0) {
        filterAttributeText = filterAttributeArray.join(", ")
      }
      let filterAttributeTemplate
      let filterTemplate
      let sortTemplate
      let setTemplate
      let setText

      if (def.title.show) {
        if (filterAttributeText) {
          filterAttributeTemplate = `<span class="__filter __filteractive" title="${tooltipAttributes}">${T(
            "SYSTableTitleFilterAttributes",
            filterAttributeText
          )}</span>`
        }

        if (filterText) {
          filterTemplate = `<span class="__filter ${filteractive}" title="${tooltip}">${T(
            "SYSTableTitleFilter",
            filterText
          )}</span>`
        }
        if (sortText) {
          sortTemplate = `<span class="__sort" title="${tooltipSort}">${T(
            "SYSTableTitleSort",
            sortText
          )}</span>`
        }
        if (
          def.options.filterSortSettings?.showInTitle &&
          def.uiState.currentSelectedFilterSortDesc &&
          (!hasSelectedFilterChanged ||
            def.title.invalidFavoritDisplayMode === "Mark")
        ) {
          setText = def.uiState.currentSelectedFilterSortDesc
          let markInvalid = ""
          if (hasSelectedFilterChanged) {
            markInvalid = "__invalid"
          }
          setTemplate = `<span class="__filtersort ${markInvalid}">${T(
            "SYSTableTitleSet",
            setText
          )}</span>`
        }
        title = `<span>${T("SYSTableTitleAndSetAndFilterAndSort", [
          titleTemplate,
          setTemplate,
          filterTemplate,
          filterAttributeTemplate,
          sortTemplate,
        ])}</span>`

        let delim = T("SYSTableTitleDelimiter")

        title = title.replaceAll(`${delim}${delim}${delim}`, `${delim}`)
        title = title.replaceAll(`${delim}${delim}`, `${delim}`)
        title = title.replaceAll(`${delim}</span>`, "</span>")
        title = title.replaceAll(` ${delim}`, " ")
        let titleAsTemplate = JSON.parse(JSON.stringify(title))
        title = unsafeHTML(title)
        if (fn?.hooks?.[HookTableCustomTitle]) {
          let titleFromHook = await fn.hooks[HookTableCustomTitle](<
            HookTableCustomTitle_Type
          >{
            title,
            titleAsTemplate,
            titleText,
            titleTemplate,
            setText,
            setTemplate,
            sortText,
            sortTemplate,
            filterText,
            filterTemplate,
            filterTextArray,
            filterAttributeText,
            filterAttributeTemplate,
            filterAttributeArray,
            staticFilter: def.uiState.filter.filter.static,
            forcedFilterKeys: def.uiState.filter.filter.forcedFilterKeys,
            forcedFilterContextTitle:
              def.uiState.filter.filter.forcedFilterContextTitle,
            defId: def.id,
          })
          if (titleFromHook) {
            title = titleFromHook
          }
        }
      }
    }

    let caption
    if (title || rowNavTop)
      caption = html`
        <caption
          class="ovl-table-title __${def.id} ovl-cursor-pointer"
          @click=${this.handleTHGroupColumnClick}
        >
          ${title} ${rowNavTop}
        </caption>
      `
    let tableBottom
    if (def.initialised) {
      tableBottom = html`<tr class="fd-table__row">
        <td class="fd-has-text-align-center" colspan=${columnsCount}>
          ${rowNavBottom}
        </td>
      </tr>`
    }
    return html`
      ${caption} ${colStyle}
      <thead class="fd-table__header ${stickyTableHeader}">
        ${groupHeaderRow}
        <tr
          @click=${this.handleHeaderColumnClick}
          class=${`fd-table__row  ovl-tableview-header header__${def.id} ${
            def.features.headerMenu ? "headermenu_enabled" : ""
          } ${def.uiState.tableFilterClass} `}
        >
          ${headerRow}
        </tr>
      </thead>

      ${rows} ${tableBottom}
    `
  }
  async afterRender() {
    if (this.tableAfterRenderHookFn && this.tabledata.def.id) {
      await this.tableAfterRenderHookFn(<HookTableRenderingAfterRender_Type>{
        defId: this.tabledata.def.id,
        comp: this,
      })
    }
    await super.afterRender()
  }
  async updated() {
    if (this.tableUpdatedHookFn && this.tabledata.def.id) {
      await this.tableUpdatedHookFn(<HookTableRenderingUpdated_Type>{
        defId: this.tabledata.def.id,
        comp: this,
      })
    }
  }
  async firstUpdated() {
    if (this.tableFirstUpdatedHookFn && this.tabledata.def.id) {
      await this.tableFirstUpdatedHookFn(<HookTableRenderingFirstUpdated_Type>{
        defId: this.tabledata.def.id,
        comp: this,
      })
    }
  }

  async connectedCallback() {
    this.addEventListener("click", this.handleRowClick)
    this.addEventListener("keydown", this.handleKeyDown)
    await super.connectedCallback()
  }

  removeDragOverClass = () => {
    let toremove = document.querySelectorAll(".ovl-dragover")
    for (let i = 0; i < toremove.length; i++) {
      toremove[i].classList.remove("ovl-dragover")
    }
  }

  foundNode = (el: HTMLElement): boolean => {
    return (
      (el as any).row?.row !== undefined ||
      el.tagName === "TH" ||
      el.tagName === "OVL-TABLE"
    )
  }
  getValidRowNode = (
    e: Event,
    eventType: DragDropEventType
  ): { found: boolean; row: any; el: HTMLElement; columnKey: string } => {
    let el: any = e.target

    let found = false
    let columnKey = undefined
    do {
      if (this.foundNode(el)) {
        found = true
      }
      if (el.getAttribute("data-col")) {
        columnKey = el.getAttribute("data-col")
      }
      if (
        eventType === "dragover" &&
        (el?.tagName === "TD" ||
          el?.tagName === "OVL-TABLE" ||
          el?.tagName === "TH")
      ) {
        if (!el.classList.contains("ovl-dragover")) {
          this.removeDragOverClass()
          if (el.tagName !== "TD" || el.classList.contains("ovltvc")) {
            el.classList.add("ovl-dragover")
          }
        }
      }
      if (found) {
        break
      }
      el = el.parentNode
    } while (el)
    return { found, el, row: el?.row, columnKey }
  }

  handleDrop = async (e) => {
    if (!DialogCurrent()) {
      ScreenCurrentState().focus.focusWhenLeft = undefined
    }
    await this.handleDragDrop("drop", e)
    this.removeDragOverClass()
  }

  handleDragOver = (e) => {
    this.handleDragDrop("dragover", e)
  }

  handleDragStart = (e) => {
    this.handleDragDrop("dragstart", e)
  }

  handleDragDrop = async (eventType: DragDropEventType, e: DragEvent) => {
    if (this.dragAndDropHookFn) {
      let validRow = this.getValidRowNode(e, eventType)

      if (validRow.found) {
        await this.dragAndDropHookFn(<HookTableDragDrop_Type>{
          type: eventType,
          e,
          row: validRow.row?.row,
          key: validRow.row?.key,
          el: validRow.el,
          formState: undefined,
          columnKey: validRow.columnKey,
          tableDefId: this.tabledata.def.id,
        })
      }
    }
  }

  handleRowClick = async (e) => {
    let currentModifiers: TableClickModifiersType = {
      altKey: e.altKey,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
    }
    let def = this.tabledata.def
    let key
    let colKey
    // expand cell?
    let expandClick = false
    // was it a rowFn?
    let targetId = e.target.id
    if (targetId.startsWith("OvlRowFn")) {
      await rowControlActionsHandler(
        e.target.data.isCustom,
        e.target.data.functionKey,
        def,
        this.tabledata.data,
        e.target.data.rowKey,
        false
      )
      return
    }

    // get first key and colKey from current target upwards in dom-tree

    let currentNode = e.target
    let linkHandledByOvl = false
    let linkHandledBySystem = false

    for (let zz = 0; zz < 5; zz++) {
      if (!linkHandledByOvl) {
        linkHandledByOvl =
          currentNode.classList &&
          currentNode.classList.contains("ovl-hook-handled")
      }
      if (!linkHandledBySystem) {
        linkHandledBySystem =
          currentNode.classList &&
          currentNode.classList.contains("ovl-sys-handled")
      }
      if (!expandClick) {
        expandClick =
          currentNode.classList &&
          currentNode.classList.contains("ovl-expand-onclick")
      }
      if (!colKey) {
        colKey = currentNode.getAttribute("data-col")
      }
      key = currentNode.getAttribute("data-rowkey")
      if (key) {
        break
      }
      currentNode = currentNode.parentElement
      if (!currentNode) {
        break
      }
    }

    if (linkHandledByOvl) {
      // helps with links
      e.preventDefault()
    }

    // was it a feedbackrowFn?
    if (targetId.startsWith("ovlfeedbackbtn")) {
      let dialogType: DialogType
      if (e.target.classList.contains("__error")) {
        dialogType = "error"
      } else if (e.target.classList.contains("__warning")) {
        dialogType = "warning"
      } else if (e.target.classList.contains("__success")) {
        dialogType = "success"
      } else if (e.target.classList.contains("__information")) {
        dialogType = "information"
      }

      if (key) {
        let title = _GetTableDialogTitle(
          def,
          T("SYSTableRowFnFeedbackOverview"),
          1,
          this.tabledata.data.data,
          key
        )
        let ovlSysState = this.tabledata.data.data[key]
          .OvlSysState as OvlSysColumn
        let feedback = ovlSysState.fnFeedback

        let details = Object.keys(feedback)
          .sort((a, b) => feedback[b].ts - feedback[a].ts)
          .map((k) => {
            let tb = TableGet(feedback[k].defId)
            let fnName = T(tb.def.options.customRowActions[k].translationKey)
            let ts = getDateValue(
              new Date().setTime(feedback[k].ts),
              "timestamp"
            )
            let feedbackMsg = feedback[k].text
            if (typeof feedbackMsg === "string") {
              feedbackMsg = unsafeHTML(<string>feedbackMsg)
            }
            return html`
              <div>
                <i
                  class="__${feedback[k].type} sap-icon--message-${feedback[k]
                    .type}"
                ></i>
                ${ts}
              </div>
              <div>${fnName}</div>
              <div>${html`${feedbackMsg}`}</div>
            `
          })

        DialogOk(
          html`<div class="ovl-feedback-overview">
            <div></div>
            <div>${T("SYSTableRowFnFeedbackOverviewColAction")}</div>
            <div>${T("SYSTableRowFnFeedbackOverviewColMessage")}</div>
            ${details}
          </div>`,
          {
            type: dialogType,
            title,
            instance: "ovl-feedback-overview",
          }
        )
      }

      return
    }

    // we need to use this hook to do e.preventDefault, stopPropagation,...
    // it is important this one is first and sync
    let functionName = HookTableClicked
    let fn = this.actions.app.features[def.feature]?.hooks?.[functionName]
    const executionToSet: _HookTableClickedExecutionToSetType = {
      selectionLogic:
        linkHandledByOvl || linkHandledBySystem ? "Ignore" : "Mode1",
      expandTextLogic: linkHandledBySystem ? "Ignore" : "Default",
    }
    if (fn) {
      let value: HookTableClicked_Type = {
        columnKey: colKey,
        defId: def.id,
        e,
        executionToSet,
        modifiers: currentModifiers,
        rowKey: key,
        row: this.tabledata.data.data[key],
        linkType: linkHandledByOvl
          ? "OvlHook"
          : linkHandledBySystem
            ? "System"
            : "None",
        expandClicked: expandClick,
      }

      Object.defineProperty(value, "executionToSet", {
        value: executionToSet,
        writable: false,
        configurable: false,
        enumerable: true,
      })

      await fn(value)
    }

    if (expandClick && executionToSet.expandTextLogic !== "Ignore") {
      if (executionToSet.expandTextLogic === "Default") {
        setTimeout(() => {
          // use a timeout because usecase bar: when clicked on row then mark as read is handled in the HookTableRowSelectedEventControlHandler which is called before this (see above).
          // if notimeout in that case the mark as read comes before the expanded text dialog
          this.showExpandTextDialog(def, colKey, key)
        }, 0)
        return
      } else {
        this.showExpandTextDialog(def, colKey, key)
      }
    }
    if (executionToSet.selectionLogic === "Ignore") {
      return
    }
    if (key) {
      TableSelectRow(def.id, key, {
        modifiers: currentModifiers,
        _innerEvent: true,
        e,
        mode: executionToSet.selectionLogic,
      })
    }
  }
  showExpandTextDialog = (def: SYSTableDef, colKey: string, key: string) => {
    DialogOk(
      GetRowDisplayValue(def, colKey, this.tabledata.data.data[key], "Table"),
      { customClass: "__expand", instance: `${def.id}${key}` }
    )
  }
  handleKeyDown = async (e: KeyboardEvent) => {
    // if keyboard is coming from inline form edit or inline form controls the ignore

    if (
      //@ts-ignore
      e.target.classList.contains("ovl-formcontrol-input")
    ) {
      return
    }

    if (!isMobile()) {
      if (e.key === "ArrowDown") {
        //@ts-ignore
        let node = e.target.parentNode
        if (!node.nextElementSibling) {
          node = node.parentNode
          if (!node.nextElementSibling) {
            node = node.parentNode
          }
        }

        node = node.nextElementSibling
        if (node && node.tagName === "OVL-TRG") {
          node = node.firstElementChild
          while (node && node.tagName !== "OVL-TROW") {
            node = node.nextElementSibling
          }
          if (node) {
            node.focus()
          }
        }
      } else if (e.key === "ArrowUp") {
        //@ts-ignore
        let node = e.target.parentNode
        if (!node.previousElementSibling) {
          node = node.parentNode
          if (!node.previousElementSibling) {
            node = node.parentNode
          }
        }
        node = node.previousElementSibling
        if (node && node.tagName === "OVL-TRG") {
          node = node.firstElementChild
          while (node && node.tagName !== "OVL-TROW") {
            node = node.nextElementSibling
          }
          if (node) {
            node.focus()
          }
        }
      } else if (
        // only do it for OVL-TROW because if on button,a, then it will be handled by browser click
        // elsewise it would be executed twice
        //@ts-ignore
        e.target?.tagName === "OVL-TROW" &&
        (e.key === " " ||
          //@ts-ignore
          e.key === "Enter")
      ) {
        this.handleRowClick(e)
      }
    }
  }
}
